import { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Chip,
  Alert,
  CircularProgress,
  Breadcrumbs,
  Link,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
} from '@mui/material';
import {
  Save as SaveIcon,
  Refresh as RefreshIcon,
  ArrowBack as ArrowBackIcon,
  RemoveCircleOutline as ExcludeIcon,
} from '@mui/icons-material';
import api from '@/services/api';
import { ReleaseNoteItem, ReleaseNoteStatus } from '@/types';

const statusColors: Record<ReleaseNoteStatus, { bg: string; color: string; label: string }> = {
  [ReleaseNoteStatus.Pending]: { bg: '#FEF3C7', color: '#92400E', label: 'Pending' },
  [ReleaseNoteStatus.Generated]: { bg: '#D1FAE5', color: '#065F46', label: 'Generated' },
  [ReleaseNoteStatus.Edited]: { bg: '#E0E7FF', color: '#3730A3', label: 'Edited' },
  [ReleaseNoteStatus.Published]: { bg: '#DBEAFE', color: '#1E40AF', label: 'Published' },
  [ReleaseNoteStatus.Failed]: { bg: '#FEE2E2', color: '#991B1B', label: 'Failed' },
  [ReleaseNoteStatus.Excluded]: { bg: '#F3E5F5', color: '#7B1FA2', label: 'Excluded' },
};

export default function Editor() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const returnTo = (location.state as { returnTo?: string } | undefined)?.returnTo ?? '/';
  
  const [releaseNote, setReleaseNote] = useState('');
  const [editableSummary, setEditableSummary] = useState('');
  const [productSuite, setProductSuite] = useState('');
  const [module, setModule] = useState('');
  const [hasChanges, setHasChanges] = useState(false);

  // Fetch item
  const { data: item, isLoading, error } = useQuery({
    queryKey: ['releaseNoteItem', id],
    queryFn: () => api.getReleaseNoteItem(id!),
    enabled: !!id,
  });

  // Fetch product suites and modules
  const { data: productSuites } = useQuery({
    queryKey: ['productSuites'],
    queryFn: () => api.getProductSuites(),
  });

  const { data: moduleMappings } = useQuery({
    queryKey: ['moduleMappings'],
    queryFn: () => api.getModuleMappings(),
  });

  // Initialize state from item
  useEffect(() => {
    if (item) {
      setReleaseNote(item.currentContent || item.generatedContent || '');
      setEditableSummary(item.summary || '');
      setProductSuite(item.productSuite || '');
      setModule(item.module || '');
    }
  }, [item]);

  // Save mutation
  const saveMutation = useMutation({
    mutationFn: (updatedItem: ReleaseNoteItem) => api.updateReleaseNoteItem(updatedItem),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['releaseNoteItem', id] });
      queryClient.invalidateQueries({ queryKey: ['releaseNoteItems'] });
      setHasChanges(false);
    },
  });

  // Regenerate mutation
  const regenerateMutation = useMutation({
    mutationFn: () => api.regenerateReleaseNote(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['releaseNoteItem', id] });
    },
  });

  // Exclude mutation
  const excludeMutation = useMutation({
    mutationFn: () => api.excludeItem(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['releaseNoteItem', id] });
      queryClient.invalidateQueries({ queryKey: ['releaseNoteItems'] });
    },
  });

  // Include mutation
  const includeMutation = useMutation({
    mutationFn: () => api.includeItem(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['releaseNoteItem', id] });
      queryClient.invalidateQueries({ queryKey: ['releaseNoteItems'] });
    },
  });

  const handleReleaseNoteChange = (value: string) => {
    setReleaseNote(value);
    setHasChanges(true);
  };

  const handleProductSuiteChange = (event: SelectChangeEvent) => {
    setProductSuite(event.target.value);
    setModule(''); // Reset module when product suite changes
    setHasChanges(true);
  };

  const handleModuleChange = (event: SelectChangeEvent) => {
    setModule(event.target.value);
    setHasChanges(true);
  };

  const handleSummaryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditableSummary(e.target.value);
    setHasChanges(true);
  };

  const handleSave = () => {
    if (!item) return;
    saveMutation.mutate({
      ...item,
      summary: editableSummary,
      currentContent: releaseNote,
      productSuite,
      module,
      status: hasChanges && item.status === ReleaseNoteStatus.Generated 
        ? ReleaseNoteStatus.Edited 
        : item.status,
    });
  };

  const handleSaveCategory = () => {
    if (!item) return;
    saveMutation.mutate({
      ...item,
      summary: editableSummary,
      productSuite,
      module,
    });
  };

  // Get modules filtered by selected product suite
  const filteredModules = (Array.isArray(moduleMappings) ? moduleMappings : []).filter(
    (m) => !productSuite || m.productSuite === productSuite
  );

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !item) {
    return (
      <Alert severity="error">
        {error ? 'Error loading item' : 'Item not found'}
      </Alert>
    );
  }

  const statusConfig = statusColors[item.status] || statusColors[ReleaseNoteStatus.Pending];
  const isExcluded = item.status === ReleaseNoteStatus.Excluded;

  return (
    <Box>
      <Breadcrumbs sx={{ mb: 3 }}>
        <Link
          component="button"
          variant="body2"
          onClick={() => navigate('/')}
          sx={{ textDecoration: 'none' }}
        >
          Dashboard
        </Link>
        <Typography color="text.primary" variant="body2">
          {item.issueKey}
        </Typography>
      </Breadcrumbs>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(returnTo)}
            variant="text"
          >
            Back
          </Button>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            {item.issueKey}
          </Typography>
        </Box>
      </Box>

      {isExcluded && (
        <Alert 
          severity="warning" 
          sx={{ mb: 3 }}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => includeMutation.mutate()}
              disabled={includeMutation.isPending}
            >
              Restore
            </Button>
          }
        >
          This item has been excluded from release notes.
        </Alert>
      )}

      {saveMutation.isError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          Error saving changes: {(saveMutation.error as Error)?.message}
        </Alert>
      )}

      {saveMutation.isSuccess && (
        <Alert severity="success" sx={{ mb: 3 }}>
          Changes saved successfully
        </Alert>
      )}

      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
          {item.issueType && (
            <Chip
              label={item.issueType}
              size="small"
              sx={{ backgroundColor: '#E5E7EB', color: '#374151' }}
            />
          )}
          <Chip
            label={statusConfig.label}
            size="small"
            sx={{ backgroundColor: statusConfig.bg, color: statusConfig.color }}
          />
          {item.productSuite && (
            <Chip
              label={item.productSuite}
              size="small"
              sx={{ backgroundColor: '#E0F2F1', color: '#00695C' }}
            />
          )}
        </Box>

        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          Summary (editable; changes are local only and do not update Jira)
        </Typography>
        <TextField
          fullWidth
          value={editableSummary}
          onChange={handleSummaryChange}
          variant="outlined"
          size="small"
          sx={{ mb: 3 }}
          placeholder="Jira ticket title"
        />

        {item.description && (
          <>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Description
            </Typography>
            <Typography variant="body2" sx={{ mb: 3, whiteSpace: 'pre-wrap' }}>
              {item.description}
            </Typography>
          </>
        )}

        {item.acceptanceCriteria && (
          <>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Acceptance Criteria
            </Typography>
            <Typography variant="body2" sx={{ mb: 3, whiteSpace: 'pre-wrap' }}>
              {item.acceptanceCriteria}
            </Typography>
          </>
        )}
      </Paper>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Category
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>Product Suite</InputLabel>
            <Select
              value={productSuite}
              label="Product Suite"
              onChange={handleProductSuiteChange}
            >
              <MenuItem value="">None</MenuItem>
              {Array.isArray(productSuites) && productSuites.map((suite) => (
                <MenuItem key={suite} value={suite}>{suite}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>Module</InputLabel>
            <Select
              value={module}
              label="Module"
              onChange={handleModuleChange}
            >
              <MenuItem value="">None</MenuItem>
              {filteredModules.map((m) => (
                <MenuItem key={m.id} value={m.jiraModule}>{m.jiraModule}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            variant="outlined"
            onClick={handleSaveCategory}
            disabled={saveMutation.isPending}
          >
            Save Category
          </Button>
        </Box>
      </Paper>

      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Generated Release Note
        </Typography>
        <TextField
          multiline
          fullWidth
          minRows={6}
          value={releaseNote}
          onChange={(e) => handleReleaseNoteChange(e.target.value)}
          disabled={isExcluded}
          placeholder="Release note content..."
          variant="outlined"
        />

        <Box sx={{ display: 'flex', gap: 2, mt: 3, justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={handleSave}
              disabled={saveMutation.isPending || isExcluded}
            >
              {saveMutation.isPending ? 'Saving...' : 'Save & Sync to Jira'}
            </Button>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={() => regenerateMutation.mutate()}
              disabled={regenerateMutation.isPending || isExcluded}
            >
              {regenerateMutation.isPending ? 'Regenerating...' : 'Regenerate'}
            </Button>
          </Box>

          {!isExcluded && (
            <Button
              variant="outlined"
              color="error"
              startIcon={<ExcludeIcon />}
              onClick={() => excludeMutation.mutate()}
              disabled={excludeMutation.isPending}
            >
              Exclude from Release
            </Button>
          )}
        </Box>
      </Paper>
    </Box>
  );
}
